import { useState, useEffect } from "react";
import { Star, Send, CheckCircle, Quote, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [formData, setFormData] = useState({
    nom: "",
    note: 5,
    service: "",
    commentaire: ""
  });

  const services = [
    { value: "Réparation mécanique", label: "Réparation mécanique" },
    { value: "Achat véhicule", label: "Achat véhicule" },
    { value: "Entretien / Révision", label: "Entretien / Révision" },
    { value: "Serrurerie automobile", label: "Serrurerie automobile" },
    { value: "Contrôle technique", label: "Contrôle technique" },
    { value: "Autre", label: "Autre" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/reviews`),
          axios.get(`${API}/settings`)
        ]);
        setReviews(reviewsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nom || !formData.commentaire) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      setSubmitted(true);
      toast.success("Merci pour votre avis !");
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Erreur lors de l'envoi de votre avis");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (note, interactive = false) => {
    const displayNote = interactive ? (hoveredStar || formData.note) : note;
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-6 h-6 cursor-pointer transition-colors ${
          i < displayNote ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 hover:text-yellow-300'
        }`}
        onClick={interactive ? () => setFormData({...formData, note: i + 1}) : undefined}
        onMouseEnter={interactive ? () => setHoveredStar(i + 1) : undefined}
        onMouseLeave={interactive ? () => setHoveredStar(0) : undefined}
      />
    ));
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.note, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div data-testid="reviews-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50">
        <div className="page-container">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            AVIS <span className="text-yellow-500">CLIENTS</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            Découvrez ce que nos clients pensent de nos services. Votre satisfaction est notre priorité.
          </p>
          {reviews.length > 0 && (
            <div className="mt-8 flex items-center gap-4">
              <div className="flex gap-1">
                {renderStars(Math.round(parseFloat(averageRating)))}
              </div>
              <span className="text-2xl font-bold text-gray-900">{averageRating}/5</span>
              <span className="text-gray-500">({reviews.length} avis)</span>
            </div>
          )}
          
          {/* Google Reviews Button */}
          {settings?.google_reviews_url && (
            <div className="mt-8">
              <a 
                href={settings.google_reviews_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white border-2 border-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold hover:border-yellow-400 hover:shadow-lg transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Lire tous les avis Google
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="section-spacing bg-white">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Reviews List */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Témoignages de nos clients
              </h2>
              
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-gray-100 rounded-xl h-40 animate-pulse" />
                  ))}
                </div>
              ) : reviews.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center">
                  <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun avis pour le moment. Soyez le premier !</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review, index) => (
                    <div 
                      key={review.id}
                      className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow animate-fade-in-up"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                            <span className="text-yellow-600 font-bold text-lg">
                              {review.nom.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{review.nom}</h3>
                            {review.service && (
                              <p className="text-sm text-yellow-600">{review.service}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-0.5">
                          {renderStars(review.note)}
                        </div>
                      </div>
                      <div className="relative">
                        <Quote className="w-8 h-8 text-yellow-100 absolute -top-2 -left-2" />
                        <p className="text-gray-600 italic pl-6">"{review.commentaire}"</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Review Form */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 sticky top-24">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Merci !</h3>
                    <p className="text-gray-600 mb-6">
                      Votre avis a bien été envoyé. Il sera publié après validation.
                    </p>
                    <Button 
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ nom: "", note: 5, service: "", commentaire: "" });
                      }}
                      variant="outline"
                    >
                      Laisser un autre avis
                    </Button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Laissez votre avis</h3>
                    <p className="text-gray-500 text-sm mb-6">
                      Partagez votre expérience avec notre garage
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
                      <div>
                        <Label htmlFor="nom" className="text-gray-700">Votre nom *</Label>
                        <Input
                          id="nom"
                          value={formData.nom}
                          onChange={(e) => setFormData({...formData, nom: e.target.value})}
                          placeholder="Jean Dupont"
                          className="mt-1"
                          data-testid="review-nom"
                        />
                      </div>

                      <div>
                        <Label className="text-gray-700">Votre note *</Label>
                        <div className="flex gap-1 mt-2" data-testid="review-stars">
                          {renderStars(formData.note, true)}
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-700">Service utilisé</Label>
                        <Select 
                          value={formData.service} 
                          onValueChange={(value) => setFormData({...formData, service: value})}
                        >
                          <SelectTrigger className="mt-1" data-testid="review-service">
                            <SelectValue placeholder="Sélectionnez un service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="commentaire" className="text-gray-700">Votre avis *</Label>
                        <Textarea
                          id="commentaire"
                          value={formData.commentaire}
                          onChange={(e) => setFormData({...formData, commentaire: e.target.value})}
                          placeholder="Partagez votre expérience..."
                          className="mt-1 min-h-[120px]"
                          data-testid="review-commentaire"
                        />
                      </div>

                      <Button 
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-yellow-500 text-white hover:bg-yellow-600"
                        data-testid="review-submit"
                      >
                        {submitting ? (
                          "Envoi en cours..."
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Envoyer mon avis
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-gray-400 text-center">
                        Votre avis sera publié après validation par notre équipe.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
